import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { Location } from '../../models/location.model';
import { HereMapApiService, HereAutosuggestItem } from '@antsway/antsroute-common/map';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-location-finder',
  templateUrl: './location-finder.component.html',
  styleUrls: ['./location-finder.component.scss'],
  standalone: false
})
export class LocationFinderComponent implements OnInit, AfterViewInit, OnDestroy {
  private hereMapService = inject(HereMapApiService);
  private settingsService = inject(SettingsService);

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  @Input() location: Location = new Location();
  @Input() form: FormGroup;
  @Input() disabled: boolean;

  @Output() locationChange = new EventEmitter<Location>();

  locationSelected = false;
  locations: HereAutosuggestItem[] = [];

  MIN_CHARACTERS = 3;

  private locationChangeSubscription: Subscription;

  get address(): AbstractControl {
    return this.form.get('address');
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.form.get('address').disable();
    } else {
      this.form.get('address').addValidators(this.locationValidator());
    }
  }

  ngAfterViewInit(): void {
    this.locationChangeSubscription = this.address.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((input) => {
      if (!this.locationSelected) {
        if (input.length >= this.MIN_CHARACTERS) {
          this.getPredictions(input);
        } else {
          this.locations = [];
          this.location.latitude = null;
          this.location.longitude = null;
        }
      } else {
        // Fix autocomplete opened after selection
        this.locationSelected = false;

        this.locations = [];
        this.autocomplete.closePanel();
      }
    });
  }

  ngOnDestroy(): void {
    this.form.get('address').clearValidators();
    this.locationChangeSubscription?.unsubscribe();
  }

  locationValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      let locationFilled = false;

      if (
        (this.locationSelected && this.location.latitude && this.location.longitude && control.value) ||
        this.form.get('address').pristine
      ) {
        locationFilled = true;
      }

      return locationFilled ? null : { unknownAddress: true };
    };
  }

  private getPredictions(input: string): void {
    this.hereMapService
      .searchPlaces(input, this.settingsService.link.order.location.latitude, this.settingsService.link.order.location.longitude)
      .subscribe({
        next: (res) => {
          this.locations = res.items || [];
          this.autocomplete.openPanel();
        },
        error: (_err) => {
          this.locations = [];
        }
      });
  }

  selectFromPredictions(place: HereAutosuggestItem): void {
    const label = place.address.label;

    this.address.setValue(label, { emitEvent: false });

    this.setLocation(label, place.position.lat, place.position.lng);

    this.locationSelected = true;
    this.locations = [];
    this.autocomplete.closePanel();
    this.address.updateValueAndValidity();
  }

  private setLocation(address: string, latitude: number, longitude: number): void {
    this.location.address = address;
    this.location.latitude = latitude;
    this.location.longitude = longitude;
    this.locationChange.next(this.location);
  }
}
